import { useCallback, useState } from 'react';
import { SIDEBAR_COLLAPSED_STORAGE_KEY } from '../constants';

interface UseSidebarResult {
  isCollapsed: boolean;
  toggleSidebar: () => void;
  setCollapsed: (collapsed: boolean) => void;
}

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(collapsed));
  } catch {
    /* storage unavailable -- preference just will not persist */
  }
}

/**
 * Collapse state is UI-only, so it lives here rather than in Redux;
 * localStorage provides the persistence.
 *
 * Read in a lazy initializer, not an effect: an effect would render expanded
 * first and then snap shut on every reload.
 */
export function useSidebar(): UseSidebarResult {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(readCollapsed);

  const setCollapsed = useCallback((collapsed: boolean) => {
    setIsCollapsed(collapsed);
    writeCollapsed(collapsed);
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsCollapsed((previous) => {
      const next = !previous;
      writeCollapsed(next);
      return next;
    });
  }, []);

  return { isCollapsed, toggleSidebar, setCollapsed };
}
